import { Controller } from "@hotwired/stimulus"

/**
 * PanelTabsController - Gestion des onglets pour mobile
 *
 * Permet de switcher entre les panneaux disponibles et sélectionnés
 * sur mobile où l'affichage en colonnes n'est pas optimal.
 *
 * Usage:
 * <div data-controller="panel-tabs">
 *   <button role="tab" data-panel-tabs-target="tab" data-action="click->panel-tabs#switchTab" data-panel-tabs-index-param="0">
 *   <div role="tabpanel" data-panel-tabs-target="panel">
 */
export default class extends Controller {
  static targets = ["tab", "panel", "availableCount", "selectedCount"]

  static values = {
    activeIndex: { type: Number, default: 0 }
  }

  connect() {
    this.updateTabStates()
  }

  switchTab(event) {
    event.preventDefault()

    const index = parseInt(event.params.index)
    if (isNaN(index)) return

    this.activeIndexValue = index
    this.updateTabStates()
  }

  updateTabStates() {
    const activeIndex = this.activeIndexValue

    // Mettre à jour les onglets
    this.tabTargets.forEach((tab, index) => {
      const isActive = index === activeIndex

      tab.setAttribute("aria-selected", isActive.toString())
      tab.setAttribute("tabindex", isActive ? "0" : "-1")

      if (isActive) {
        tab.classList.add("text-blue-600", "border-blue-600")
        tab.classList.remove("text-gray-500", "border-transparent")
      } else {
        tab.classList.remove("text-blue-600", "border-blue-600")
        tab.classList.add("text-gray-500", "border-transparent")
      }
    })

    // Mettre à jour les panneaux (seulement sur mobile)
    this.panelTargets.forEach((panel, index) => {
      const isActive = index === activeIndex

      // Sur mobile (lg:hidden), afficher/masquer
      // Sur desktop, toujours afficher (géré par lg:block)
      if (window.innerWidth < 1024) {
        if (isActive) {
          panel.classList.remove("hidden")
        } else {
          panel.classList.add("hidden")
        }
      }
    })
  }

  // Appelé quand la fenêtre est redimensionnée
  handleResize() {
    // Sur desktop, afficher les deux panneaux
    if (window.innerWidth >= 1024) {
      this.panelTargets.forEach(panel => {
        panel.classList.remove("hidden")
      })
    } else {
      // Sur mobile, appliquer l'état actif
      this.updateTabStates()
    }
  }

  // Navigation clavier entre onglets
  handleTabKeydown(event) {
    const tabs = this.tabTargets
    const currentIndex = tabs.indexOf(event.target)

    switch (event.key) {
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault()
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1
        tabs[prevIndex]?.focus()
        break

      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault()
        const nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0
        tabs[nextIndex]?.focus()
        break

      case "Home":
        event.preventDefault()
        tabs[0]?.focus()
        break

      case "End":
        event.preventDefault()
        tabs[tabs.length - 1]?.focus()
        break
    }
  }
}
